import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, share, switchMap, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import { BaseEntityService } from '../../../shared/services';
import { Instance, ZoningCode, ZoningCodeFactory, ZoningCodeVersion } from '../models';
import { Task, TaskType } from '../../../shared';
import { ZoningCodeVersionService } from './zoning-code-version.service';

import Response = ZoningAPI.Response;
import IZoningCodeIdentity = ZoningAPI.IZoningCodeIdentity;
import IZoningCodeRecord = ZoningAPI.IZoningCodeRecord;

import { InstanceService } from './instance.service';
import { TaskService } from './task.service';

import { MatSnackBar } from '@angular/material/snack-bar';
import * as _ from 'lodash';
import { ITaskModel } from 'src/app/shared/interfaces/task-model.interface';
import { PropertyRecordRelationType } from 'src/app/shared/enums/property-record-relation-type';
import { ISyncZoningCodeParams } from 'src/app/shared/interfaces/sync-zoning-code-params.interface';
import { IGetZoningCodesParams } from '../interfaces/get-zoning-codes-params.interface';
import { IIndexationTaskParams } from '../interfaces/indexation-task-params.interface';

const API_ENDPOINT = environment.ZONAR_API_URL + '/zoning-code';
const COMMON_API_ENDPOINT = environment.ZONAR_API_URL + '/common-data';

@Injectable({
    providedIn: 'root'
})
export class ZoningCodeService extends BaseEntityService<ZoningCode> {
    private readonly zoningCodesSubject = new BehaviorSubject<ZoningCode[]>([]);
    // needed for all tasks data - zoning codes from not selected instance
    private readonly allZoningCodesSubject = new BehaviorSubject<ZoningCode[]>([]);
    private readonly newZoningCodesSubject = new BehaviorSubject<ZoningCode[]>([]);

    // needed for all tasks data - zoning codes from not selected instance
    readonly allZoningCodes = this.allZoningCodesSubject.asObservable().pipe(share());
    readonly zoningCodes = this.zoningCodesSubject.asObservable().pipe(share(), distinctUntilChanged());
    readonly newZoningCodes = this.newZoningCodesSubject.asObservable().pipe(share());

    constructor(
        private readonly http: HttpClient,
        private readonly taskService: TaskService,
        private readonly zcVersionService: ZoningCodeVersionService,
        private readonly instanceService: InstanceService,
        public readonly snackBar: MatSnackBar
    ) {
        super(ZoningCodeFactory);

        this.instanceService.currentInstance
            .pipe(
                filter(instance => instance !== null),
                switchMap(instance => this.getZoningCodes(instance, { getOrphanZoningCodes: true }))
            )
            .subscribe(zoningCodes => this.setZoningCodes(zoningCodes));

        combineLatest([this.zoningCodes, this.taskService.processes]).subscribe(([_zoningCodes, tasks]) => {
            this.updateZoningCodesTasks(tasks);
            this.instanceService.updateSelectedInstanceStatistic();
        });
    }

    getZoningCodesByIds(zcData: Array<IZoningCodeIdentity>) {
        const headers = new HttpHeaders().set('Content-Type', 'application/json');

        return this.http
            .post<Response>(
                `${COMMON_API_ENDPOINT}/zoning-codes`,
                { zoningCodesData: zcData, getStatistic: true },
                { headers }
            )
            .pipe(
                map(response => this.initZoningCodes(response.data)),
                tap(zoningCodes => this.allZoningCodesSubject.next(this.addOrUpdateZoningCodesList(zoningCodes)))
            );
    }

    get lastZoningCodesValue() {
        return this.zoningCodesSubject.value;
    }

    getZoningCodes(instance: Instance, getZCParams?: IGetZoningCodesParams) {
        const paramsObj: any = {
            fromObject: {
                gridicsInstanceId: String(instance.Id),
                getZCRecords: 'true'
            }
        };

        if (getZCParams.getOrphanZoningCodes) {
            paramsObj.fromObject.getOrphanZoningCodes = 'true';
        }

        const params = new HttpParams(paramsObj);

        return this.http
            .get<Response>(API_ENDPOINT, { params })
            .pipe(
                map(response => this.initZoningCodes(response.data)),
                tap(zoningCodes => this.allZoningCodesSubject.next(this.addOrUpdateZoningCodesList(zoningCodes)))
            );
    }

    private addOrUpdateZoningCodesList(zoningCodesData: Array<ZoningCode>) {
        const newZoningCodes: Array<ZoningCode> = [];
        if (zoningCodesData && zoningCodesData.length) {
            for (const zcData of zoningCodesData) {
                const existingZC = this.allZoningCodesSubject.value.find(zc => zc.Id === zcData.Id);
                if (!existingZC) {
                    const zoningCode = this.initEntity(zcData);
                    newZoningCodes.push(zoningCode);
                } else {
                    existingZC.initWithJSON(existingZC);
                }
            }
        }
        return newZoningCodes.length
            ? [...this.allZoningCodesSubject.value, ...newZoningCodes]
            : this.allZoningCodesSubject.value;
    }

    private requestNewZoningCodes(id: number): Observable<ZoningCode[]> {
        const params = new HttpParams({ fromObject: { gridicsInstanceId: String(id) } });

        return this.http
            .get<Response>(`${API_ENDPOINT}/unregistered/`, { params })
            .pipe(
                map(response => this.initZoningCodes(response.data)),
                tap(zoningCodes => this.allZoningCodesSubject.next(this.addOrUpdateZoningCodesList(zoningCodes)))
            );
    }

    initZoningCodes(data: any) {
        return this.initEntities(data);
    }

    getNewZoningCodes(instance: Instance) {
        this.requestNewZoningCodes(instance.Id).subscribe(zoningCodes => this.setNewZoningCodes(zoningCodes));
    }

    getAttachmentLinksForZC(zcId: number) {
        if (!Number.isInteger(zcId)) {
            return;
        }

        return this.http.get<Response>(`${environment.ZONAR_API_URL}/zoning-code/${zcId}/attachments`);
    }

    getIndexedFoliosAttachment(zcId: number) {
        if (!Number.isInteger(zcId)) {
            return;
        }

        return this.http.post<Response>(
            `${environment.ZONAR_API_URL}/zoning-code/${zcId}/attachments/indexed-properties`,
            {}
        );
    }

    registerNewZoningCodeId(zoningCode: ZoningCode) {
        zoningCode.PropertyRecordsCount = 0;
        const newZoningCodes = this.newZoningCodesSubject.value.filter(zc => zc.Id !== zoningCode.Id);

        return this.http.put(API_ENDPOINT, { Id: zoningCode.Id }).pipe(
            tap((arrivedZoningCode: any) => {
                const zc: ZoningCode = new ZoningCode(arrivedZoningCode.data);
                this.setZoningCodes([...this.zoningCodesSubject.value, zc]);
                this.setNewZoningCodes(newZoningCodes);
                this.uploadVersionsForZoningCodeAndEmitChanges(zc);
            })
        );
    }

    uploadVersionsForZoningCodeAndEmitChanges(zoningCode: ZoningCode, stateEnv?: string) {
        if (!zoningCode) {
            return;
        }

        this.uploadVersionsForZoningCode(zoningCode, stateEnv)
            .pipe(
                map(versions => {
                    if (versions && versions.length) {
                        this.updateZoningCodes();
                    }
                })
            )
            .subscribe();
    }

    uploadVersionsForZoningCode(zoningCode: ZoningCode, stateEnv?: string): Observable<ZoningCodeVersion[]> {
        if (!zoningCode) {
            return of([]);
        }

        return this.zcVersionService.getVersionsForZoningCode(zoningCode, stateEnv).pipe(
            map(versions => {
                if (versions && versions.length) {
                    zoningCode.Versions = versions;
                    zoningCode.ActualVersion = versions[versions.length - 1].ActualVersion;
                    return versions;
                }
                return [];
            })
        );
    }

    checkZoningCodeVersion(zoningCode: ZoningCode) {
        this.zcVersionService.checkZoningCodeVersion(zoningCode).subscribe(response => {
            zoningCode.ActualVersion = response.ActualVersion;
            zoningCode.Versions = [...zoningCode.Versions, response];
            this.updateZoningCodes();
        });
    }

    indexZCPropertyRecords(params: IIndexationTaskParams) {
        if (!params || !params.instance || !params.zoningCodeId || !params.zcGridicsId) {
            return;
        }

        this.taskService.getIndexZCPropertyRecordsTask(params).subscribe();
    }

    indexZCPropertyRecordsWithCursor(params: IIndexationTaskParams) {
        if (!params || !params.instance || !params.zoningCodeId || !params.zcGridicsId) {
            return;
        }

        this.taskService.getIndexZCPropertyRecordsWithCursorTask(params).subscribe();
    }

    checkZC(zoningCode: ZoningCode) {
        return this.taskService.checkStatusOfTaskByZC(
            zoningCode,
            [TaskType.ZONING_CODE_INDEXING, TaskType.ZONING_CODE_INDEXING_WITH_CURSOR],
            t => t.zoningCodeId === zoningCode.Id && Task.isIndexing(t)
        );
    }

    checkZCVersion(zoningCode: ZoningCode, zcVersion: ZoningCodeVersion, stateEnv?: string) {
        const type: TaskType = TaskType.ZC_VERSION_CALCULATION;
        return this.taskService.checkStatusOfTaskByZC(
            zoningCode,
            type,
            t => t.zcVersionId === zcVersion.Id && t.type === type,
            false,
            stateEnv
        );
    }

    updateZoningCodesTasks(tasks: ITaskModel[]) {
        this.zoningCodesSubject.value.map(zoningCode => zoningCode.updateTasksFromList(tasks));
    }

    getTypologiesByZoningCodeId(zcId: number) {
        if (!Number.isInteger(zcId)) {
            return;
        }

        return this.http.get<Response>(`${environment.ZONAR_API_URL}/zoning-code/${zcId}/typologies`);
    }

    deleteZCVersion(zcVersionId: number, zoningCode: ZoningCode) {
        if (!Number.isInteger(zcVersionId) || !zoningCode) {
            return;
        }

        const versionIndex = zoningCode.Versions.findIndex(zcVersion => zcVersion.Id === zcVersionId);

        zoningCode.Versions.splice(versionIndex, 1);

        // slice is needed to update data source in table
        zoningCode.Versions = zoningCode.Versions.slice();

        this.updateZoningCodes();
    }

    removeZoningCode(zoningCodeId: number) {
        if (!Number.isInteger(zoningCodeId)) {
            return;
        }

        const zoningCodes = this.zoningCodesSubject.getValue().filter(zoningCode => zoningCode.Id !== zoningCodeId);

        this.setZoningCodes(zoningCodes);
    }

    deleteZoningCode(zoningCodeId: number) {
        if (!zoningCodeId) {
            return;
        }

        return this.http
            .delete<Response>(`${API_ENDPOINT}/${zoningCodeId}`)
            .pipe(tap(() => this.removeZoningCode(zoningCodeId)));
    }

    /**
     * TODO :: need to remove /zoning-code/statistic call and use Zoning Code MetaData.
     */
    /**
     * Admin panel uses state interceptor to set state_env parameter.
     * It works if state is selected in other case it will not add state_env parameter.
     * That is why it is better to be able to set these parameter manually.
     *
     */
    uploadAndSetZoningCodeStatistic(
        zoningCode: ZoningCode,
        getPerformanceStatistic: boolean = false,
        getStatByZCStatuses: boolean = false,
        getLastCalculationStat: boolean = false,
        getLastIndexationStat: boolean = false
    ): Observable<void> {
        if (!zoningCode) {
            return of();
        }

        const fromObject: any = {
            zcGridicsId: String(zoningCode.GridicsId),
            getLastCalculationStat: String(getLastCalculationStat),
            getLastIndexationStat: String(getLastIndexationStat)
        };

        if (getPerformanceStatistic) {
            fromObject.getPerformanceStatistic = 'true';
        }

        if (getStatByZCStatuses) {
            fromObject.getStatByZCStatuses = 'true';
        }

        const params = new HttpParams({ fromObject });

        return this.http
            .get<Response>(`${API_ENDPOINT}/statistic`, { params })
            .pipe(
                map(response => response.data),
                tap(data => zoningCode.setStatistic(data))
            );
    }

    getZoningCodeCalculationStatistic(
        zoningCode: ZoningCode,
        zcVersion: ZoningCodeVersion,
        stateEnv?: string,
        getLastCalculationStat: boolean = false,
        getLastIndexationStat: boolean = false
    ) {
        const fromObject: any = {
            zcGridicsId: String(zoningCode.GridicsId),
            zcVersionId: String(zcVersion.Id),
            getLastCalculationStat: String(getLastCalculationStat),
            getLastIndexationStat: String(getLastIndexationStat)
        };

        if (stateEnv) {
            fromObject.state_env = stateEnv;
        }

        const params = new HttpParams({
            fromObject
        });

        return this.http
            .get<Response>(`${API_ENDPOINT}/calculation-statistic`, { params })
            .pipe(
                map(response => response.data),
                tap(calculationStatistic => {
                    if (calculationStatistic) {
                        zcVersion.setStatistic(calculationStatistic);
                        zcVersion.setDeletionStatus(false);
                    } else {
                        zoningCode.unregisterVersionById(zcVersion.Id);
                        this.snackBar.open(
                            'Requested Zoning Code Version does not exist any more and was unregistered',
                            'OK',
                            {
                                verticalPosition: 'top',
                                horizontalPosition: 'center',
                                duration: 2000
                            }
                        );
                    }
                })
            );
    }

    getZoningCodeById(id: number) {
        const res = this.zoningCodesSubject.value.find(zc => zc.Id === id);
        return res || this.allZoningCodesSubject.value.find(zc => zc.Id === id);
    }

    updateZoningCodes() {
        this.setZoningCodes(this.zoningCodesSubject.value);
    }

    /**
     * This method triggers Zoning Code Sync API which
     * updates Zoning Code record and statistic, basic on params
     */
    updateZoningCodeStatus(
        zoningCode: ZoningCode,
        stateEnv: string,
        syncParams?: ISyncZoningCodeParams
    ): Observable<IZoningCodeRecord> {
        if (!zoningCode) {
            return of();
        }

        const fromObject: any = {
            id: String(zoningCode.Id),
            syncZCMissedEntitiesStatistic: String(_.get(syncParams, 'syncZCMissedEntitiesStatistic', false))
        };

        if (stateEnv) {
            fromObject.state_env = stateEnv;
        }

        if (syncParams?.syncStatistic) {
            fromObject.syncStatistic = 'true';
        }

        if (syncParams?.syncZCStatus) {
            fromObject.syncZCStatus = 'true';
        }

        if (syncParams?.getDefaultDSC) {
            fromObject.getDefaultDSC = 'true';
        }

        if (syncParams?.syncStatByZCStatuses) {
            fromObject.syncStatByZCStatuses = 'true';
        }

        if (syncParams?.syncPerformanceStatistic) {
            fromObject.syncPerformanceStatistic = 'true';
        }

        if (syncParams?.syncSolrData) {
            fromObject.syncSolrData = 'true';
        }

        const params = new HttpParams({ fromObject });

        return this.http
            .post<Response>(`${API_ENDPOINT}/sync`, null, { params })
            .pipe(
                map(response => response.data),
                map(data => {
                    const zoningCodeRecord = data[0];
                    if (syncParams?.updateZCRecord) {
                        zoningCode.setZoningCodeRecord(zoningCodeRecord);
                    } else {
                        return zoningCodeRecord;
                    }
                })
            );
    }

    getZoningCodeRecordById(
        zoningCodeId: number,
        state_env: string,
        getDefaultDSC: boolean = false
    ): Observable<IZoningCodeRecord> {
        if (!zoningCodeId || !state_env) {
            return of(null);
        }

        const fromObject: any = {
            state_env: state_env
        };

        if (getDefaultDSC) {
            fromObject.getDefaultDSC = 'true';
        }

        const params = new HttpParams({ fromObject });

        return this.http
            .get<Response>(`${API_ENDPOINT}/${zoningCodeId}`, { params })
            .pipe(map(response => response.data));
    }

    generateMissedEntitiesAttachment(zoningCodeId: number, relationType: PropertyRecordRelationType) {
        if (!Number.isInteger(zoningCodeId) || !Number.isInteger(relationType)) {
            return of(null);
        }

        return this.http.post<Response>(
            `${environment.ZONAR_API_URL}/zoning-code/${zoningCodeId}/attachments/missed-entities?relationType=${relationType}`,
            {}
        );
    }

    setZoningCodes(zoningCodes: ZoningCode[]) {
        zoningCodes = zoningCodes || [];
        const activeZoningCodes = zoningCodes.filter(zc => !zc.isOrphan);
        const orphanZoningCodes = zoningCodes.filter(zc => zc.isOrphan);
        this.zoningCodesSubject.next([..._.sortBy(activeZoningCodes, 'Name'), ..._.sortBy(orphanZoningCodes, 'Name')]);
    }

    setNewZoningCodes(zoningCodes: ZoningCode[]) {
        zoningCodes = zoningCodes || [];
        this.newZoningCodesSubject.next(_.sortBy(zoningCodes, 'Name'));
    }
}
